import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/AdminLayout";
import { ClientStatusBadge } from "@/components/Badges";
import { api, errMsg, money, fmtDate } from "@/lib/api";

const TABS = ["all", "pending", "approved", "disabled"];

function AddClientDialog({ open, onClose, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/clients", f);
      toast.success("Client added");
      setF({ name: "", email: "", phone: "", notes: "" });
      onCreated();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="add-client-dialog">
        <DialogHeader><DialogTitle>Add client manually</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {["name", "email", "phone"].map((k) => (
            <div key={k} className="space-y-1.5">
              <Label className="capitalize">{k}</Label>
              <Input required={k !== "phone"} type={k === "email" ? "email" : "text"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`add-client-${k}-input`} />
            </div>
          ))}
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="add-client-notes-input" />
          </div>
          <p className="text-xs text-muted-foreground">Manually added clients are approved. Set a password on their profile if they should log in.</p>
          <Button type="submit" disabled={busy} data-testid="add-client-submit-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Add client</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Clients() {
  const nav = useNavigate();
  const [tab, setTab] = useState("all");
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/clients", { params: tab === "all" ? {} : { status: tab } }).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [tab]);
  useEffect(() => {
    load();
  }, [load]);

  const approve = async (e, c) => {
    e.stopPropagation();
    try {
      await api.patch(`/admin/clients/${c.id}`, { status: "approved" });
      toast.success(`${c.name} approved`);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const shown = useMemo(() => list.filter((c) => `${c.name} ${c.email} ${c.phone}`.toLowerCase().includes(q.toLowerCase())), [list, q]);

  return (
    <div>
      <PageHeader eyebrow="CRM" title="Clients">
        <Button onClick={() => setAdding(true)} className="gap-2" data-testid="clients-add-button"><Plus className="h-4 w-4" />Add client</Button>
      </PageHeader>
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`clients-tab-${t}`}
              className={`rounded-full border px-4 py-1.5 text-sm capitalize transition-colors ${tab === t ? "border-emerald-500 bg-emerald-500/10 text-emerald-300" : "border-border text-muted-foreground hover:text-foreground"}`}>{t}</button>
          ))}
        </div>
        <div className="relative md:w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search clients" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="clients-search-input" />
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Status</TableHead>
              <TableHead>Orders</TableHead><TableHead>Spent</TableHead><TableHead>Last order</TableHead><TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((c) => (
              <TableRow key={c.id} className="cursor-pointer" onClick={() => nav(`/admin/clients/${c.id}`)} data-testid={`client-row-${c.id}`}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{c.email}{c.phone && <div className="font-mono-data text-xs">{c.phone}</div>}</TableCell>
                <TableCell><ClientStatusBadge status={c.status} /></TableCell>
                <TableCell className="font-mono-data">{c.order_count}</TableCell>
                <TableCell className="font-mono-data">{money(c.total_spent)}</TableCell>
                <TableCell className="text-muted-foreground">{fmtDate(c.last_order)}</TableCell>
                <TableCell>
                  {c.status === "pending" && (
                    <Button size="sm" onClick={(e) => approve(e, c)} data-testid={`admin-approve-client-button-${c.id}`} className="gap-1"><Check className="h-3.5 w-3.5" />Approve</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {shown.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground" data-testid="clients-empty">No clients yet. Share an invite code to get started.</p>}
      </div>
      <AddClientDialog open={adding} onClose={() => setAdding(false)} onCreated={load} />
    </div>
  );
}
